import React, { useEffect, useRef, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Card,
	CardContent,
	Typography,
	Box,
	Tooltip as MuiTooltip,
	Chip
} from '@material-ui/core';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { getThemeColors } from '../utils/themeUtils';
import ClusterInfoDialog from './ClusterInfoDialog';

const useStyles = makeStyles(theme => ({
	card: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none',
		display: 'flex',
		flexDirection: 'column'
	},
	cardContent: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		'&:last-child': {
			paddingBottom: theme.spacing(3)
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(2)
	},
	title: {
		fontSize: '18px',
		fontWeight: 600,
		color: '#f8fafc'
	},
	mapContainer: {
		width: '100%',
		height: '431px',
		position: 'relative',
		backgroundColor: '#0f172a',
		borderRadius: '8px',
		overflow: 'hidden',
		border: '1px solid #1e293b'
	},
	mapContainerEmbedded: {
		width: '100%',
		height: '100%',
		position: 'relative',
		backgroundColor: 'transparent',
		borderRadius: 0,
		overflow: 'hidden',
		border: 'none'
	},
	legend: {
		display: 'flex',
		gap: theme.spacing(2),
		flexWrap: 'wrap'
	},
	legendOverlay: {
		position: 'absolute',
		bottom: '16px',
		right: '16px',
		display: 'flex',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
		zIndex: 10,
		backgroundColor: 'rgba(15, 23, 42, 0.85)',
		padding: '8px 12px',
		borderRadius: '8px',
		backdropFilter: 'blur(10px)',
		border: '1px solid rgba(255, 255, 255, 0.1)'
	},
	tooltip: {
		backgroundColor: '#1e293b',
		color: '#f8fafc',
		padding: theme.spacing(2),
		borderRadius: '8px',
		boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
		maxWidth: 300,
		border: '1px solid #334155'
	},
	clusterItem: {
		cursor: 'pointer',
		padding: '6px 8px',
		margin: '0 -8px',
		borderRadius: '4px',
		transition: 'background-color 0.15s ease',
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.1)'
		}
	}
}));

const WorldMapVisualization = ({ clusters, embedded = false, themeMode = 'dark', userView = false }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);
	const svgRef = useRef();
	const tooltipRef = useRef(null);
	const hideTimeoutRef = useRef(null);
	const [tooltipData, setTooltipData] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
	const [isTooltipHovered, setIsTooltipHovered] = useState(false);
	
	// Dialog state for cluster info
	const [selectedCluster, setSelectedCluster] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);

	// Provider accordion state for grouped tooltips - reset when tooltip changes
	const [expandedProviders, setExpandedProviders] = useState({});
	useEffect(function() { setExpandedProviders({}); }, [tooltipData]);

	// Handle cluster click - open dialog instead of navigating directly
	const handleClusterClick = useCallback((cluster) => {
		setSelectedCluster(cluster);
		setDialogOpen(true);
		setTooltipData(null); // Close tooltip when dialog opens
	}, []);

	// Handle dialog close
	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setSelectedCluster(null);
	}, []);

	// Clear hide timeout when component unmounts or when entering tooltip
	const clearHideTimeout = useCallback(() => {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
	}, []);

	// Schedule hiding tooltip with delay
	const scheduleHideTooltip = useCallback(() => {
		clearHideTimeout();
		hideTimeoutRef.current = setTimeout(() => {
			if (!isTooltipHovered) {
				setTooltipData(null);
			}
		}, 300); // 300ms delay to allow moving mouse to tooltip
	}, [clearHideTimeout, isTooltipHovered]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!clusters || clusters.length === 0) return;
		if (!svgRef.current) return;

		const container = svgRef.current;
		const width = container.clientWidth || 800;
		const height = embedded ? (container.clientHeight || 500) : 431; // Dynamic height for embedded, fixed for card

		// Clear previous content
		d3.select(svgRef.current).selectAll('*').remove();

		// Create SVG
		const svg = d3
			.select(svgRef.current)
			.append('svg')
			.attr('width', '100%')
			.attr('height', '100%')
			.attr('viewBox', `0 0 ${width} ${height}`)
			.attr('preserveAspectRatio', 'xMidYMid meet');

		// Filter out clusters without valid coordinates
		const validClusters = clusters.filter(
			d => d.location && 
			     typeof d.location.longitude === 'number' && 
			     typeof d.location.latitude === 'number' &&
			     !isNaN(d.location.longitude) &&
			     !isNaN(d.location.latitude)
		);

		// Calculate bounds for clusters
		let bounds = null;
		if (validClusters.length > 0) {
			const lons = validClusters.map(d => d.location.longitude);
			const lats = validClusters.map(d => d.location.latitude);
			
			// Expand bounds to show more context
			const minLon = Math.min(...lons);
			const maxLon = Math.max(...lons);
			const minLat = Math.min(...lats);
			const maxLat = Math.max(...lats);
			
			// Add minimum span to ensure we show continent context
			const lonSpan = Math.max(maxLon - minLon, 30); // At least 30 degrees longitude
			const latSpan = Math.max(maxLat - minLat, 20); // At least 20 degrees latitude
			
			const centerLon = (minLon + maxLon) / 2;
			const centerLat = (minLat + maxLat) / 2;
			
			bounds = [
				[centerLon - lonSpan / 2, centerLat - latSpan / 2],
				[centerLon + lonSpan / 2, centerLat + latSpan / 2]
			];
		}

		// Create projection with dynamic zoom
		const projection = d3.geoMercator();
		
		if (bounds && validClusters.length > 0) {
			// Fit projection to cluster bounds with padding
			const [[x0, y0], [x1, y1]] = bounds;
			const dx = x1 - x0;
			const dy = y1 - y0;
			const centerX = (x0 + x1) / 2;
			const centerY = (y0 + y1) / 2;
			
			// Calculate scale to fit bounds (with more padding - zoom out more)
			const scaleX = (width * 0.7) / (dx * Math.PI / 180);  // Changed from 0.9 to 0.7
			const scaleY = (height * 0.7) / (dy * Math.PI / 180); // Changed from 0.9 to 0.7
			const scale = Math.min(scaleX, scaleY);
			
			projection
				.center([centerX, centerY])
				.scale(scale)
				.translate([width / 2, height / 2]);
		} else {
			// Default world view
			projection
				.scale(110)  // Changed from 130 to 110 for more zoom out
				.translate([width / 2, height / 1.5]);
		}

		const path = d3.geoPath().projection(projection);

		// Load world map data
		d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
			.then(world => {
				const countries = feature(world, world.objects.countries);

				// Draw map
				svg
					.append('g')
					.selectAll('path')
					.data(countries.features)
					.enter()
					.append('path')
					.attr('d', path)
					.attr('fill', colors.map.landFill)
					.attr('stroke', colors.map.landStroke)
					.attr('stroke-width', 0.5);

		// Group clusters by location (within 50km / ~0.5 degrees)
		const clusterGroups = [];
		const groupThreshold = 0.5; // degrees
		
		validClusters.forEach(cluster => {
			let addedToGroup = false;
			
			for (let group of clusterGroups) {
				const groupCenter = group[0];
				const latDiff = Math.abs(cluster.location.latitude - groupCenter.location.latitude);
				const lonDiff = Math.abs(cluster.location.longitude - groupCenter.location.longitude);
				
				if (latDiff < groupThreshold && lonDiff < groupThreshold) {
					group.push(cluster);
					addedToGroup = true;
					break;
				}
			}
			
			if (!addedToGroup) {
				clusterGroups.push([cluster]);
			}
		});
		
		// Create group data with aggregated info
		const groupData = clusterGroups.map(group => {
			const avgLat = group.reduce((sum, c) => sum + c.location.latitude, 0) / group.length;
			const avgLon = group.reduce((sum, c) => sum + c.location.longitude, 0) / group.length;
			const totalNodes = group.reduce((sum, c) => sum + (c.nodeCount || 0), 0);
			const activeCount = group.filter(c => {
				const state = c.state ? c.state.toLowerCase() : '';
				return state === 'available';
			}).length;
			// Count transitioning clusters (deploying, destroying, starting, stopping)
			const transitionCount = group.filter(c => {
				const state = c.state ? c.state.toLowerCase() : '';
				return state === 'deploying' || state === 'destroying' || state === 'starting' || state === 'stopping';
			}).length;
			
			return {
				location: { latitude: avgLat, longitude: avgLon },
				clusters: group,
				count: group.length,
				totalNodes: totalNodes,
				activeCount: activeCount,
				transitionCount: transitionCount,
				// Use the state of the first cluster for single clusters
				state: group.length === 1 ? group[0].state : 'grouped'
			};
		});

		// Draw cluster markers
		const clusterMarkerGroups = svg
			.append('g')
			.selectAll('g')
			.data(groupData)
			.enter()
			.append('g')
			.style('cursor', 'pointer');
			
		// Cluster circles
		clusterMarkerGroups
			.append('circle')
			.attr('cx', d => projection([d.location.longitude, d.location.latitude])[0])
			.attr('cy', d => projection([d.location.longitude, d.location.latitude])[1])
			.attr('r', d => Math.max(12, Math.sqrt(d.totalNodes || 0) * 3))
			.attr('fill', d => {
				return colors.card.background;
			})
		.attr('opacity', 0.7)
		.attr('stroke', d => {
			// Always green for user dashboard
			if (userView) {
				return colors.accent.green;
			}
			const state = d.state ? d.state.toLowerCase() : '';
			if (d.count > 1) {
				// Grouped: orange if any transitioning, green if any active, gray if all inactive
				if (d.transitionCount > 0) return colors.status.transition;
				return d.activeCount > 0 ? colors.accent.green : colors.text.muted;
			}
			// Single cluster: orange for transitioning, green for active, gray for inactive, red for error
			if (state === 'deploying' || state === 'destroying' || state === 'starting' || state === 'stopping') {
				return colors.status.transition;
			}
			if (state === 'available') {
				return colors.accent.green;
				}
				if (state === 'error' || state === 'failed' || state === 'offline') {
					return colors.accent.red;
				}
				return colors.text.muted;
			})
			.attr('stroke-width', 2)
			.attr('class', d => {
				if (d.count === 1) {
					const state = d.state ? d.state.toLowerCase() : '';
					if (state === 'available' || state === 'deploying' || state === 'starting') {
						return 'pulse';
					}
				}
				return '';
			});
		
		// Add total count text inside ALL cluster circles (visible by default)
		clusterMarkerGroups
			.append('text')
			.attr('class', 'count-total')
			.attr('x', d => projection([d.location.longitude, d.location.latitude])[0])
			.attr('y', d => projection([d.location.longitude, d.location.latitude])[1])
			.attr('text-anchor', 'middle')
			.attr('dominant-baseline', 'central')
			.attr('fill', colors.text.primary)
			.attr('font-size', '14px')
			.attr('font-weight', 'bold')
			.attr('pointer-events', 'none')
			.text(d => d.count);
		
		// Add fraction text for ALL clusters (hidden by default, shown on hover)
		clusterMarkerGroups
			.append('text')
			.attr('class', 'count-fraction')
			.attr('x', d => projection([d.location.longitude, d.location.latitude])[0])
			.attr('y', d => projection([d.location.longitude, d.location.latitude])[1])
			.attr('text-anchor', 'middle')
			.attr('dominant-baseline', 'central')
			.attr('fill', colors.text.primary)
			.attr('font-size', '11px')
			.attr('font-weight', 'bold')
			.attr('pointer-events', 'none')
		.attr('opacity', 0)
		.html(d => {
			// Show only total count in green for user dashboard
			if (userView) {
				return `<tspan fill="${colors.accent.green}" font-size="18px" font-weight="bold">${d.count}</tspan>`;
			}
			// Show fraction: active/total with active number bigger
			return `<tspan fill="${colors.accent.green}" font-size="18px" font-weight="bold">${d.activeCount}</tspan><tspan fill="${colors.text.muted}" font-size="13px">/</tspan><tspan fill="${colors.text.muted}" font-size="13px">${d.count}</tspan>`;
		});		// Add region labels below cluster markers (subtle styling)
		clusterMarkerGroups
			.append('text')
			.attr('class', 'region-label')
			.attr('x', d => projection([d.location.longitude, d.location.latitude])[0])
			.attr('y', d => {
				const baseRadius = Math.max(12, Math.sqrt(d.totalNodes || 0) * 3);
				return projection([d.location.longitude, d.location.latitude])[1] + baseRadius + 25;
			})
			.attr('text-anchor', 'middle')
			.attr('fill', colors.text.tertiary)
			.attr('font-size', '12px')
			.attr('font-weight', '400')
			.attr('opacity', 0.8)
			.attr('pointer-events', 'none')
			.attr('letter-spacing', '0.5px')
			.text(d => {
				// Show region from first cluster in group
				if (d.clusters && d.clusters.length > 0) {
					const region = d.clusters[0].region || d.clusters[0].location?.city || 'No Region';
					return region;
				}
				return 'Unknown';
			});
		
		// Hide overlapping region labels by default
		const labelVerticalSpacing = 30; // Minimum vertical spacing between labels
		const labelHorizontalSpacing = 80; // Minimum horizontal spacing between labels
		
		clusterMarkerGroups.each(function(d, i) {
			const currentLabel = d3.select(this).select('.region-label');
			const currentPos = projection([d.location.longitude, d.location.latitude]);
			const currentY = parseFloat(currentLabel.attr('y'));
			
			// Check against all previous labels
			clusterMarkerGroups.each(function(otherD, j) {
				if (j >= i) return; // Only check previous labels to avoid hiding both
				
				const otherLabel = d3.select(this).select('.region-label');
				const otherPos = projection([otherD.location.longitude, otherD.location.latitude]);
				const otherY = parseFloat(otherLabel.attr('y'));
				
				// Calculate distances
				const horizontalDist = Math.abs(currentPos[0] - otherPos[0]);
				const verticalDist = Math.abs(currentY - otherY);
				
				// If labels are too close, hide the current one (keep the first one)
				if (horizontalDist < labelHorizontalSpacing && verticalDist < labelVerticalSpacing) {
					currentLabel.attr('opacity', 0);
					currentLabel.attr('data-hidden-by-proximity', 'true');
				}
			});
		});
		
		// Add hover interactions
		clusterMarkerGroups
			.on('mouseover', function (event, d) {
				const currentCircle = d3.select(this).select('circle');
				const currentGroup = d3.select(this);
				const expandedRadius = Math.max(30, Math.sqrt(d.totalNodes || 0) * 7);
				const currentPos = projection([d.location.longitude, d.location.latitude]);
				
				// Expand the hovered circle
				currentCircle
					.transition()
					.duration(200)
					.attr('r', expandedRadius)
					.attr('opacity', 1);
				
				// Keep the current region label visible and emphasized
				currentGroup.select('.region-label')
					.transition()
					.duration(200)
					.attr('opacity', 1)
					.attr('font-weight', '600');
				
				// Hide total count and show fraction for ALL clusters on hover
				d3.select(this).select('.count-total')
					.transition()
					.duration(200)
					.attr('opacity', 0);
				
				d3.select(this).select('.count-fraction')
					.transition()
					.duration(200)
					.attr('opacity', 1);
				
				// Check for overlapping circles and hide them with fade
				clusterMarkerGroups.each(function(otherD, i) {
					if (otherD === d) return; // Skip the hovered circle
					
					const otherGroup = d3.select(this);
					const otherPos = projection([otherD.location.longitude, otherD.location.latitude]);
					const otherRadius = Math.max(12, Math.sqrt(otherD.totalNodes || 0) * 3);
					
					// Calculate distance between centers
					const distance = Math.sqrt(
						Math.pow(currentPos[0] - otherPos[0], 2) + 
						Math.pow(currentPos[1] - otherPos[1], 2)
					);
					
					// If circles are close (overlap or labels would conflict), hide the other group
					// Use a larger threshold to account for region labels below circles
					const labelSpacing = 60; // Extra spacing to account for region labels
					if (distance < expandedRadius + otherRadius + labelSpacing) {
						// Fade out the overlapped circle and its labels
						otherGroup
							.transition()
							.duration(200)
							.attr('opacity', 0);
					}
				});
				
				// Calculate smart tooltip position (avoid edges)
				const tooltipWidth = 320; // max-width from styles + padding
				const tooltipHeight = d.count > 1 ? 300 : 250; // larger estimate for scrollable content
				const margin = 30;
				
				let x = event.pageX + 15;
				let y = event.pageY + 15;
				
				// Check right edge
				if (x + tooltipWidth > window.innerWidth - margin) {
					x = event.pageX - tooltipWidth - 15;
				}
				
				// Check bottom edge - move tooltip above cursor if needed
				if (y + tooltipHeight > window.innerHeight - margin) {
					y = Math.max(margin, window.innerHeight - tooltipHeight - margin);
				}
				
				// Check top edge
				if (y < margin) {
					y = margin;
				}
				
				// Check left edge
				if (x < margin) {
					x = margin;
				}
				
				// Clear any pending hide timeout when showing tooltip
				clearHideTimeout();
				
				// Always show tooltip with region/location info (even for single clusters)
				setTooltipData({
					clusters: d.clusters,
					count: d.count,
					totalNodes: d.totalNodes,
					activeCount: d.activeCount
				});
				setTooltipPosition({ x, y });
			})
			.on('mouseout', function (event, d) {
				const currentGroup = d3.select(this);
				
				currentGroup.select('circle')
					.transition()
					.duration(200)
					.attr('r', d => Math.max(12, Math.sqrt(d.totalNodes || 0) * 3))
					.attr('opacity', 0.7);
				
				// Restore region label to original style, respecting proximity hiding
				const currentLabel = currentGroup.select('.region-label');
				const wasHiddenByProximity = currentLabel.attr('data-hidden-by-proximity') === 'true';
				
				currentLabel
					.transition()
					.duration(200)
					.attr('opacity', wasHiddenByProximity ? 0 : 0.8)
					.attr('font-weight', '400');
				
				// Show total count and hide fraction for ALL clusters on mouseout
				currentGroup.select('.count-total')
					.transition()
					.duration(200)
					.attr('opacity', 1);
				
				currentGroup.select('.count-fraction')
					.transition()
					.duration(200)
					.attr('opacity', 0);
				
				// Restore all hidden circles and labels with fade in
				clusterMarkerGroups
					.transition()
					.duration(200)
					.attr('opacity', 1);
				
				// Schedule tooltip hide with delay to allow user to move to tooltip
				scheduleHideTooltip();
			});

		// Pulse animation for active clusters
		const style = document.createElement('style');
		style.innerHTML = `
			@keyframes pulse {
				0%, 100% { opacity: 0.7; }
				50% { opacity: 1; }
			}
			.pulse {
				animation: pulse 2s ease-in-out infinite;
			}
		`;
		if (!document.querySelector('style[data-pulse]')) {
			style.setAttribute('data-pulse', 'true');
			document.head.appendChild(style);
		}
		})
		.catch(error => {
			console.error('Error loading world map:', error);
			// Fallback: just show cluster markers without map
			// Filter out clusters without valid coordinates
			const validClusters = clusters.filter(
				d => d.location && 
				     typeof d.location.longitude === 'number' && 
				     typeof d.location.latitude === 'number' &&
				     !isNaN(d.location.longitude) &&
				     !isNaN(d.location.latitude)
			);
			
			svg
				.selectAll('circle')
				.data(validClusters)
				.enter()
				.append('circle')
				.attr('cx', d => projection([d.location.longitude, d.location.latitude])[0])
				.attr('cy', d => projection([d.location.longitude, d.location.latitude])[1])
				.attr('r', 8)
				.attr('fill', d => {
					const state = d.state?.toLowerCase();
					if (state === 'available' || state === 'deploying' || state === 'starting') {
						return '#1C75D2'; // Blue
					}
					if (state === 'failed' || state === 'offline') {
						return '#F44336';
					}
					return '#94a3b8'; // Light gray
				})
		});
}, [clusters, embedded, userView, clearHideTimeout, scheduleHideTooltip]);	const statusCounts = clusters.reduce(
		(acc, cluster) => {
			const state = cluster.state ? cluster.state.toLowerCase() : '';
			if (state === 'available') {
				acc.active++;
			} else if (state === 'deploying' || state === 'destroying' || state === 'starting' || state === 'stopping') {
				acc.transition++;
			} else if (state === 'failed' || state === 'offline') {
				acc.error++;
			} else {
				acc.other++;
			}
			return acc;
		},
		{ active: 0, transition: 0, error: 0, other: 0 }
	);

	// Calculate total nodes (including head nodes) for user view
	const totalNodes = clusters.reduce((sum, cluster) => {
		const nodes = cluster.nodeCount || 0;
		const headNodes = 1; // Each cluster has 1 head node
		return sum + nodes + headNodes;
	}, 0);

	// If embedded, return just the map container without card wrapper
	if (embedded) {
		return (
			<div style={{ position: 'relative', width: '100%', height: '100%' }}>
				<Box className={classes.mapContainerEmbedded} ref={svgRef} />
				<Box 
					className={classes.legendOverlay}
					style={{
						backgroundColor: themeMode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.85)',
						border: `1px solid ${colors.border.primary}`
					}}
				>
					{userView ? (
						<>
							<Chip
								label={`${clusters.length} Available`}
								size="small"
								style={{ backgroundColor: colors.accent.blue, color: 'white', fontSize: '12px', height: '24px' }}
							/>
							<Chip
								label={`${totalNodes} Nodes`}
								size="small"
								style={{ backgroundColor: '#16a34a', color: 'white', fontSize: '12px', height: '24px' }}
							/>
						</>
					) : (
						<>
							<Chip
								label={`${statusCounts.active} Active`}
								size="small"
								style={{ backgroundColor: colors.accent.blue, color: 'white', fontSize: '12px', height: '24px' }}
							/>
							{statusCounts.transition > 0 && (
								<Chip
									label={`${statusCounts.transition} In Progress`}
									size="small"
									style={{ backgroundColor: colors.status.transition, color: 'white', fontSize: '12px', height: '24px' }}
								/>
							)}
							<Chip
								label={`${statusCounts.other} Inactive`}
								size="small"
								style={{ backgroundColor: colors.text.muted, color: 'white', fontSize: '12px', height: '24px' }}
							/>
							{statusCounts.error > 0 && (
								<Chip
									label={`${statusCounts.error} Error`}
									size="small"
									style={{ backgroundColor: colors.accent.red, color: 'white', fontSize: '12px', height: '24px' }}
								/>
							)}
						</>
					)}
				</Box>
				
				{/* Custom Tooltip - Interactive */}
				{tooltipData && (
					<div
						ref={tooltipRef}
						className={classes.tooltip}
						style={{
							position: 'fixed',
							left: tooltipPosition.x,
							top: tooltipPosition.y,
							zIndex: 1000,
							maxHeight: '400px',
							overflowY: 'auto'
						}}
						onMouseEnter={() => {
							clearHideTimeout();
							setIsTooltipHovered(true);
						}}
						onMouseLeave={() => {
							setIsTooltipHovered(false);
							setTooltipData(null);
						}}
					>
						{tooltipData.clusters ? (
							// Grouped clusters tooltip
							<>
								<Typography variant="h6" gutterBottom style={{ color: '#f8fafc', fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
									{tooltipData.clusters[0]?.region || tooltipData.clusters[0]?.location?.city || 'Unknown Region'}
								</Typography>
								
							{/* Summary stats - all available for user dashboard */}
							<div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
								<Typography variant="body2" style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px' }}>
									<span style={{ color: '#22c55e', fontWeight: 'bold' }}>{tooltipData.count} Available</span>
									{' • '}
									<span style={{ color: '#64748b', fontSize: '12px' }}>Click to view cluster</span>
								</Typography>
									<Typography variant="body2" style={{ fontSize: '13px', color: '#94a3b8' }}>
										Total Nodes: {tooltipData.totalNodes}
									</Typography>
								</div>
								
								{/* Cluster list grouped by provider - collapsible accordions */}
								<div style={{ marginTop: '8px', maxHeight: '300px', overflowY: 'auto' }}>
									{(function() {
										// Group clusters by provider
										var providerGroups = {};
										tooltipData.clusters.forEach(function(c) {
											var p = (c.provider || 'unknown').toUpperCase();
											if (!providerGroups[p]) providerGroups[p] = [];
											providerGroups[p].push(c);
										});
										var providerNames = Object.keys(providerGroups).sort();

										return providerNames.map(function(provName) {
											var groupClusters = providerGroups[provName];
											var isExpanded = expandedProviders[provName] || false;

											return (
												<div key={provName} style={{ marginBottom: '4px' }}>
													{/* Provider header */}
													<div
														onClick={function(e) {
															e.stopPropagation();
															setExpandedProviders(function(prev) {
																var next = Object.assign({}, prev);
																next[provName] = !prev[provName];
																return next;
															});
														}}
														style={{
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															padding: '6px 8px',
															margin: '0 -8px',
															borderRadius: '4px',
															cursor: 'pointer',
															transition: 'background-color 0.15s ease',
														}}
														onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
														onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
													>
														<Typography variant="body2" style={{ fontSize: '12px', color: '#e2e8f0', fontWeight: 600 }}>
															<span style={{ fontSize: '10px', marginRight: '6px', color: '#64748b' }}>{isExpanded ? '▼' : '▶'}</span>
															{provName}
														</Typography>
														<span style={{
															fontSize: '10px',
															fontWeight: 600,
															color: '#94a3b8',
															backgroundColor: 'rgba(255,255,255,0.1)',
															padding: '1px 6px',
															borderRadius: '8px',
														}}>
															{groupClusters.length}
														</span>
													</div>
													{/* Expanded cluster items */}
													{isExpanded && groupClusters
														.sort(function(a, b) {
															var aState = (a.state || '').toLowerCase();
															var bState = (b.state || '').toLowerCase();
															var aActive = aState === 'available' || aState === 'deploying' || aState === 'starting';
															var bActive = bState === 'available' || bState === 'deploying' || bState === 'starting';
															if (aActive && !bActive) return -1;
															if (!aActive && bActive) return 1;
															return 0;
														})
														.map(function(cluster, idx) {
															if (userView) {
																return (
																	<div key={idx} className={classes.clusterItem} onClick={function() { handleClusterClick(cluster); }}
																		style={{ paddingLeft: '24px' }}>
																		<Typography variant="body2" style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: '1.4' }}>
																			<span style={{ color: '#22c55e', fontWeight: 'bold' }}>●</span>{' '}
																			{cluster.clusterName} (Available, {cluster.nodeCount || 0} nodes)
																		</Typography>
																	</div>
																);
															}
															var state = cluster.state ? cluster.state.toLowerCase() : '';
															var statusLabel = 'Down';
															var statusColor = '#94a3b8';
															if (state === 'available') { statusLabel = 'Active'; statusColor = '#22c55e'; }
															else if (state === 'deploying') { statusLabel = 'Deploying'; statusColor = colors.status.transition; }
															else if (state === 'destroying') { statusLabel = 'Destroying'; statusColor = colors.status.transition; }
															else if (state === 'starting') { statusLabel = 'Starting'; statusColor = colors.status.transition; }
															else if (state === 'stopping') { statusLabel = 'Stopping'; statusColor = colors.status.transition; }
															else if (state === 'failed' || state === 'offline') { statusLabel = 'Error'; statusColor = '#ef4444'; }
															return (
																<div key={idx} className={classes.clusterItem} onClick={function() { handleClusterClick(cluster); }}
																	style={{ paddingLeft: '24px' }}>
																	<Typography variant="body2" style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: '1.4' }}>
																		<span style={{ color: statusColor, fontWeight: 'bold' }}>●</span>{' '}
																		{cluster.clusterName} ({statusLabel}, {cluster.nodeCount || 0} nodes)
																	</Typography>
																</div>
															);
														})
													}
												</div>
											);
										});
									})()}
								</div>
							</>
						) : (
						// Single cluster tooltip
						<>
							<Typography variant="h6" gutterBottom style={{ color: '#f8fafc', fontSize: '16px', fontWeight: 600 }}>
								{tooltipData.clusterName || tooltipData.name}
							</Typography>
							<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
								Status: <strong style={{ color: userView ? '#22c55e' : '#f8fafc' }}>{userView ? 'Available' : (tooltipData.state || tooltipData.status)}</strong>
							</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									Provider: <strong style={{ color: '#f8fafc' }}>{tooltipData.provider}</strong>
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									Nodes: <strong style={{ color: '#f8fafc' }}>{tooltipData.nodeCount || tooltipData.nodes || 0}</strong>
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									GPUs: <strong style={{ color: '#f8fafc' }}>{tooltipData.gpuCount || tooltipData.gpus || 0}</strong>
								</Typography>
								{(tooltipData.gpuUtilization !== undefined) && (
									<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px' }}>
										GPU Utilization: <strong style={{ color: '#f8fafc' }}>{tooltipData.gpuUtilization.toFixed(1)}%</strong>
									</Typography>
								)}
							</>
						)}
					</div>
				)}
				
				{/* Cluster Info Dialog */}
				<ClusterInfoDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					cluster={selectedCluster}
					themeMode={themeMode}
				/>
			</div>
		);
	}

	return (
		<Card className={classes.card}>
			<CardContent className={classes.cardContent}>
				<Box className={classes.header}>
					<Typography className={classes.title}>Global Cluster Distribution</Typography>
					<Box className={classes.legend}>
						<Chip
							label={`${statusCounts.active} Active`}
							size="small"
							style={{ backgroundColor: '#1C75D2', color: 'white' }}
						/>
						{statusCounts.transition > 0 && (
							<Chip
								label={`${statusCounts.transition} In Progress`}
								size="small"
								style={{ backgroundColor: colors.status.transition, color: 'white' }}
							/>
						)}
						<Chip
							label={`${statusCounts.other} Inactive`}
							size="small"
							style={{ backgroundColor: '#94a3b8', color: 'white' }}
						/>
						{statusCounts.error > 0 && (
							<Chip
								label={`${statusCounts.error} Error`}
								size="small"
								style={{ backgroundColor: '#F44336', color: 'white' }}
							/>
						)}
					</Box>
				</Box>

				<Box className={classes.mapContainer} ref={svgRef} />

				{/* Custom Tooltip - Interactive */}
				{tooltipData && (
					<div
						ref={tooltipRef}
						className={classes.tooltip}
						style={{
							position: 'fixed',
							left: tooltipPosition.x,
							top: tooltipPosition.y,
							zIndex: 1000,
							maxHeight: '400px',
							overflowY: 'auto'
						}}
						onMouseEnter={() => {
							clearHideTimeout();
							setIsTooltipHovered(true);
						}}
						onMouseLeave={() => {
							setIsTooltipHovered(false);
							setTooltipData(null);
						}}
					>
						{tooltipData.clusters ? (
							// Grouped clusters tooltip
							<>
								<Typography variant="h6" gutterBottom style={{ color: '#f8fafc', fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
									{tooltipData.clusters[0]?.region || tooltipData.clusters[0]?.location?.city || 'Unknown Region'}
								</Typography>
								
								{/* Summary stats */}
								<div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
									<Typography variant="body2" style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px' }}>
										<span style={{ color: '#22c55e', fontWeight: 'bold' }}>{tooltipData.activeCount} Active</span>
										{' • '}
										<span style={{ color: '#64748b', fontWeight: 'bold' }}>{tooltipData.count - tooltipData.activeCount} Down</span>
										{' '}
										<span style={{ color: '#94a3b8' }}>({tooltipData.count} {tooltipData.count === 1 ? 'Cluster' : 'Clusters'})</span>
									</Typography>
									<Typography variant="body2" style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px' }}>
										Total Nodes: {tooltipData.totalNodes}
									</Typography>
									<Typography variant="body2" style={{ fontSize: '12px', color: '#64748b' }}>
										Click a cluster to view details
									</Typography>
								</div>
								
								{/* Cluster list grouped by provider - collapsible accordions */}
								<div style={{ marginTop: '8px', maxHeight: '300px', overflowY: 'auto' }}>
									{(function() {
										var providerGroups = {};
										tooltipData.clusters.forEach(function(c) {
											var p = (c.provider || 'unknown').toUpperCase();
											if (!providerGroups[p]) providerGroups[p] = [];
											providerGroups[p].push(c);
										});
										var providerNames = Object.keys(providerGroups).sort();

										return providerNames.map(function(provName) {
											var groupClusters = providerGroups[provName];
											var isExpanded = expandedProviders[provName] || false;

											return (
												<div key={provName} style={{ marginBottom: '4px' }}>
													<div
														onClick={function(e) {
															e.stopPropagation();
															setExpandedProviders(function(prev) {
																var next = Object.assign({}, prev);
																next[provName] = !prev[provName];
																return next;
															});
														}}
														style={{
															display: 'flex', alignItems: 'center', justifyContent: 'space-between',
															padding: '6px 8px', margin: '0 -8px', borderRadius: '4px',
															cursor: 'pointer', transition: 'background-color 0.15s ease',
														}}
														onMouseEnter={function(e) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
														onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
													>
														<Typography variant="body2" style={{ fontSize: '12px', color: '#e2e8f0', fontWeight: 600 }}>
															<span style={{ fontSize: '10px', marginRight: '6px', color: '#64748b' }}>{isExpanded ? '▼' : '▶'}</span>
															{provName}
														</Typography>
														<span style={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', backgroundColor: 'rgba(255,255,255,0.1)', padding: '1px 6px', borderRadius: '8px' }}>
															{groupClusters.length}
														</span>
													</div>
													{isExpanded && groupClusters
														.sort(function(a, b) {
															var aActive = ['available','deploying','starting'].indexOf((a.state||'').toLowerCase()) !== -1;
															var bActive = ['available','deploying','starting'].indexOf((b.state||'').toLowerCase()) !== -1;
															if (aActive && !bActive) return -1;
															if (!aActive && bActive) return 1;
															return 0;
														})
														.map(function(cluster, idx) {
															var state = cluster.state ? cluster.state.toLowerCase() : '';
															var statusLabel = 'Down'; var statusColor = '#94a3b8';
															if (state === 'available') { statusLabel = 'Active'; statusColor = '#22c55e'; }
															else if (state === 'deploying') { statusLabel = 'Deploying'; statusColor = colors.status.transition; }
															else if (state === 'destroying') { statusLabel = 'Destroying'; statusColor = colors.status.transition; }
															else if (state === 'starting') { statusLabel = 'Starting'; statusColor = colors.status.transition; }
															else if (state === 'stopping') { statusLabel = 'Stopping'; statusColor = colors.status.transition; }
															else if (state === 'failed' || state === 'offline') { statusLabel = 'Error'; statusColor = '#ef4444'; }
															return (
																<div key={idx} className={classes.clusterItem} onClick={function() { handleClusterClick(cluster); }}
																	style={{ paddingLeft: '24px' }}>
																	<Typography variant="body2" style={{ fontSize: '12px', color: '#e2e8f0', lineHeight: '1.4' }}>
																		<span style={{ color: statusColor, fontWeight: 'bold' }}>●</span>{' '}
																		{cluster.clusterName} ({statusLabel}, {cluster.nodeCount || 0} nodes)
																	</Typography>
																</div>
															);
														})
													}
												</div>
											);
										});
									})()}
								</div>
							</>
						) : (
							// Single cluster tooltip
							<>
								<Typography variant="h6" gutterBottom style={{ color: '#f8fafc', fontSize: '16px', fontWeight: 600 }}>
									{tooltipData.clusterName || tooltipData.name}
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									Status: <strong style={{ color: '#f8fafc' }}>{tooltipData.state || tooltipData.status}</strong>
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									Provider: <strong style={{ color: '#f8fafc' }}>{tooltipData.provider}</strong>
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									Nodes: <strong style={{ color: '#f8fafc' }}>{tooltipData.nodeCount || tooltipData.nodes || 0}</strong>
								</Typography>
								<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '4px' }}>
									GPUs: <strong style={{ color: '#f8fafc' }}>{tooltipData.gpuCount || tooltipData.gpus || 0}</strong>
								</Typography>
								{(tooltipData.gpuUtilization !== undefined) && (
									<Typography variant="body2" style={{ color: '#cbd5e1', fontSize: '13px' }}>
										GPU Utilization: <strong style={{ color: '#f8fafc' }}>{tooltipData.gpuUtilization.toFixed(1)}%</strong>
									</Typography>
								)}
							</>
						)}
					</div>
				)}

				{/* Cluster Info Dialog */}
				<ClusterInfoDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					cluster={selectedCluster}
					themeMode={themeMode}
				/>
			</CardContent>
		</Card>
	);
};

export default WorldMapVisualization;
